// Which standaard and version an effect from the LEGACY /database catalogue
// belongs to.
//
// Deliberately separate from standaardVersions.js. That module pins versions
// the api publishes and can verify against `/versions`; these two numbers are
// an assertion. /database carries no version field at all (verified: every
// path and query variant returns a byte-identical document), so there is
// nothing to check them against.
//
// PROVENANCE — read before changing a number here. arbeidsparticipatie 0.9 and
// gelijke-kansen 1.0 were given by the meetstandaard team via the product owner
// on 2026-08-17. They are not fetched, not derivable, and not verifiable from
// the API today. When those two sectors move into the versioned pipeline the
// published documents become the source of truth and this module retires; until
// then a wrong number here silently mislabels every legacy answer's provenance.
//
// Note that 0.9 means "release candidate for 1.0" in this team's convention, so
// arbeidsparticipatie content can still change before it is final, while
// gelijke-kansen at 1.0 is settled.
//
// AI-CONTEXT: See docs/features/msi-version-stamping.md

/**
 * Legacy sector slug → asserted version of that standaard.
 * @type {Object<string, string>}
 */
const LEGACY_STANDAARD_VERSIONS = {
    arbeidsparticipatie: '0.9',
    'gelijke-kansen': '1.0',
}

/**
 * @typedef {object} LegacyStandaard
 * @property {string} standaard - Sector slug, stored as the row's `Standaard`
 * @property {string} standaardVersion - Asserted version, stored as `StandaardVersion`
 */

/**
 * Resolves an effect's `sectors` array to the standaard it was adopted from.
 *
 * Returns `null` rather than a partial answer whenever the sector is not a
 * single known legacy one. Callers must treat null as "leave the stamp empty
 * and report it", never as a reason to fall back to a default: a wrong
 * provenance stamp is worse than a missing one, because only the missing one
 * is visible.
 *
 * Every /database effect carries exactly one sector (verified against the live
 * catalogue: 31 effects, none with zero or multiple), so the multi-sector guard
 * is defensive rather than expected.
 *
 * @param {Array<string>|undefined|null} sectors - `OutputEffects.sectors` / catalogue `effect.sectors`
 * @returns {LegacyStandaard|null}
 */
const legacyStandaardFor = (sectors) => {
    if (!Array.isArray(sectors) || sectors.length !== 1) return null

    const standaard = sectors[0]
    // hasOwnProperty, niet een platte lookup: de sector komt uit de database, en
    // 'constructor' zou anders een functie als versienummer opleveren.
    const standaardVersion = Object.prototype.hasOwnProperty.call(LEGACY_STANDAARD_VERSIONS, standaard)
        ? LEGACY_STANDAARD_VERSIONS[standaard]
        : undefined
    if (!standaardVersion) return null

    return { standaard, standaardVersion }
}

/**
 * Effect-MSIId → de sector waar dat effect in de legacy-catalogus onder valt.
 *
 * Vier `/database`-effecten dragen geen `sectors` op hun tenant-doc, en geen
 * enkele organisatie die ze adopteerde draagt dat wel — er valt dus niets af te
 * leiden, niet uit een uid en niet van een andere organisatie. De sector is op
 * 2026-08-28 opgegeven door de product owner.
 *
 * ALLEEN DE SECTOR STAAT HIER, niet de versie. Die komt uit
 * LEGACY_STANDAARD_VERSIONS hierboven, zodat het versienummer van een sector op
 * precies één plek staat en niet per effect kan gaan afwijken.
 *
 * Dit is iets ánders dan internalStandaardVersions.js: dat gaat over eigen
 * (Deccos-)standaarden met een eigen versieregime, zoals de participatieladder.
 * Deze vier zijn gewone arbeidsparticipatie-effecten met een gat in hun data.
 *
 * @type {Object<string, string>}
 */
const LEGACY_EFFECT_SECTORS = {
    '68bd35c3-85d5-4fec-81ca-e094710759bc': 'arbeidsparticipatie', // Zelf-effectiviteit
    '2498b0dd-5adc-47c7-8a70-756d233645e9': 'arbeidsparticipatie', // Dagelijks functioneren
    'c6ed62e9-003d-4e54-9e39-735e700b619c': 'arbeidsparticipatie', // Maatschappelijke Participatie
    '00e572e7-bfce-4ff7-b295-b2871d7c9e28': 'arbeidsparticipatie', // Digitale vaardigheden
}

/**
 * De opgegeven sector van een effect, of null als er niets over vastligt.
 *
 * Null betekent "gebruik de gewone afleiding" — nooit een gok.
 *
 * @param {string|undefined|null} msiId - MSIId van het EFFECT, niet van een vraag
 * @returns {string|null}
 */
const legacySectorForEffect = (msiId) => {
    if (typeof msiId !== 'string' || msiId === '') return null
    // hasOwnProperty: de sleutel komt uit de database.
    if (!Object.prototype.hasOwnProperty.call(LEGACY_EFFECT_SECTORS, msiId)) return null
    return LEGACY_EFFECT_SECTORS[msiId]
}

export default legacyStandaardFor
export { LEGACY_STANDAARD_VERSIONS, LEGACY_EFFECT_SECTORS, legacySectorForEffect }
